// Natural-break interstitial ads (preview until AdMob interstitials ship).

import { shouldUseRewardedAdPreview } from "./rewardedAdPreview";

/** Minimum time between forced interstitials. */
export const INTERSTITIAL_MIN_GAP_MS = 6 * 60 * 1000;

/** No forced interstitials during the first 15 minutes of each app session. */
export const INTERSTITIAL_SESSION_GRACE_MS = 15 * 60 * 1000;

/** Maximum forced interstitials per calendar day. */
export const INTERSTITIAL_DAILY_CAP = 4;

/** Preview playback length for interstitials. */
export const INTERSTITIAL_AD_PREVIEW_DURATION_SEC = 5;

/** @deprecated Use INTERSTITIAL_MIN_GAP_MS — kept for older imports. */
export const PERIODIC_INTERSTITIAL_INTERVAL_MS = INTERSTITIAL_MIN_GAP_MS;

export const INTERSTITIAL_NATURAL_BREAK_SOURCES = ["game", "recap"];

export const INTERSTITIAL_ALLOWED_DESTINATIONS = ["home", "story", "endless"];

export const INTERSTITIAL_BLOCKED_SCREENS = [
    "recap",
    "store",
    "collection",
    "profile",
    "cosmetics",
    "achievements",
    "friends",
    "friendProfile",
    "leaderboards",
    "game",
];

const LS_INTERSTITIAL_DAY_KEY = "mmu_interstitial_ad_day_v1";
const LS_INTERSTITIAL_COUNT_KEY = "mmu_interstitial_ad_count_v1";
const LS_INTERSTITIAL_LAST_SHOWN_KEY = "mmu_interstitial_ad_last_shown_v1";
const SS_INTERSTITIAL_SESSION_START_KEY = "mmu_interstitial_session_start_v1";

function todayKey(now = Date.now()) {
    return new Date(now).toISOString().slice(0, 10);
}

function readDailyUsage(now = Date.now()) {
    try {
        const day = localStorage.getItem(LS_INTERSTITIAL_DAY_KEY);
        const count = Number(localStorage.getItem(LS_INTERSTITIAL_COUNT_KEY));
        if (day !== todayKey(now) || !Number.isFinite(count)) {
            return { day: todayKey(now), count: 0 };
        }
        return { day, count: Math.max(0, Math.floor(count)) };
    } catch {
        return { day: todayKey(now), count: 0 };
    }
}

function writeDailyUsage(count, now = Date.now()) {
    try {
        localStorage.setItem(LS_INTERSTITIAL_DAY_KEY, todayKey(now));
        localStorage.setItem(
            LS_INTERSTITIAL_COUNT_KEY,
            String(Math.max(0, Math.floor(count)))
        );
    } catch {
        // ignore localStorage failures
    }
}

function readLastShownAtMs() {
    try {
        const value = Number(localStorage.getItem(LS_INTERSTITIAL_LAST_SHOWN_KEY));
        return Number.isFinite(value) && value > 0 ? value : 0;
    } catch {
        return 0;
    }
}

function writeLastShownAtMs(now = Date.now()) {
    try {
        localStorage.setItem(
            LS_INTERSTITIAL_LAST_SHOWN_KEY,
            String(Math.max(0, Math.floor(Number(now) || 0)))
        );
    } catch {
        // ignore localStorage failures
    }
}

export function ensureInterstitialSessionStarted(now = Date.now()) {
    try {
        if (!sessionStorage.getItem(SS_INTERSTITIAL_SESSION_START_KEY)) {
            sessionStorage.setItem(
                SS_INTERSTITIAL_SESSION_START_KEY,
                String(now)
            );
        }
    } catch {
        // ignore sessionStorage failures
    }
}

export function getInterstitialSessionStartMs(now = Date.now()) {
    try {
        const raw = sessionStorage.getItem(SS_INTERSTITIAL_SESSION_START_KEY);
        const parsed = Number(raw);
        if (Number.isFinite(parsed) && parsed > 0) {
            return parsed;
        }
    } catch {
        // ignore sessionStorage failures
    }
    return now;
}

export function recordInterstitialShown(now = Date.now()) {
    const usage = readDailyUsage(now);
    writeDailyUsage(usage.count + 1, now);
    writeLastShownAtMs(now);
}

export function evaluateInterstitialEligibility(now = Date.now()) {
    const sessionStart = getInterstitialSessionStartMs(now);
    const sessionElapsed = Math.max(0, now - sessionStart);
    const usage = readDailyUsage(now);
    const remainingToday = Math.max(0, INTERSTITIAL_DAILY_CAP - usage.count);
    const lastShownAt = readLastShownAtMs();
    const msSinceLastShown =
        lastShownAt > 0 ? Math.max(0, now - lastShownAt) : INTERSTITIAL_MIN_GAP_MS;
    const msUntilNextEligible = Math.max(0, INTERSTITIAL_MIN_GAP_MS - msSinceLastShown);
    const msUntilGraceEnds = Math.max(
        0,
        INTERSTITIAL_SESSION_GRACE_MS - sessionElapsed
    );

    if (sessionElapsed < INTERSTITIAL_SESSION_GRACE_MS) {
        return {
            allowed: false,
            reason: "session-grace",
            remainingToday,
            msUntilNextEligible,
            msUntilGraceEnds,
            usedToday: usage.count,
        };
    }

    if (usage.count >= INTERSTITIAL_DAILY_CAP) {
        return {
            allowed: false,
            reason: "daily-cap",
            remainingToday: 0,
            msUntilNextEligible,
            msUntilGraceEnds: 0,
            usedToday: usage.count,
        };
    }

    if (lastShownAt > 0 && msSinceLastShown < INTERSTITIAL_MIN_GAP_MS) {
        return {
            allowed: false,
            reason: "min-gap",
            remainingToday,
            msUntilNextEligible,
            msUntilGraceEnds: 0,
            usedToday: usage.count,
        };
    }

    return {
        allowed: true,
        reason: null,
        remainingToday,
        msUntilNextEligible: 0,
        msUntilGraceEnds: 0,
        usedToday: usage.count,
    };
}

export function isNaturalBreakTransition(fromScreen = "", toScreen = "") {
    const from = String(fromScreen || "").trim();
    const to = String(toScreen || "").trim();
    if (!INTERSTITIAL_NATURAL_BREAK_SOURCES.includes(from)) {
        return false;
    }
    return INTERSTITIAL_ALLOWED_DESTINATIONS.includes(to);
}

export function isInterstitialBlockedScreen(screen = "") {
    return INTERSTITIAL_BLOCKED_SCREENS.includes(String(screen || "").trim());
}

/**
 * True while the player is actively on a live game board (cards in play).
 */
export function isGameBoardActive(state = null) {
    if (!state || state.screen !== "game") {
        return false;
    }

    if (state.showStoryIntro) {
        return false;
    }

    if (!state.runStartedAt) {
        return false;
    }

    if (state.showLevelCompleteOverlay) {
        return false;
    }

    if (state.showHeartEmptyOverlay) {
        return false;
    }

    return true;
}

export function shouldUseInterstitialAdPreview() {
    return shouldUseRewardedAdPreview();
}

export function canShowInterstitial(
    state = null,
    now = Date.now(),
    { adsRemoved = false } = {}
) {
    if (adsRemoved) {
        return false;
    }

    if (!state) {
        return false;
    }

    if (state.showInterstitialAdPreview || state.showRewardedAdPreview) {
        return false;
    }

    if (isGameBoardActive(state)) {
        return false;
    }

    if (isInterstitialBlockedScreen(state.screen)) {
        return false;
    }

    return evaluateInterstitialEligibility(now).allowed;
}

/** @deprecated Use canShowInterstitial — kept for transitional imports. */
export function canShowPeriodicInterstitial(state = null, options = {}) {
    return canShowInterstitial(state, Date.now(), options);
}
